import { t } from '@lingui/core/macro';
import { Currency, MonetaryValue } from '@luminovo/commons';
import { Box } from '@mui/material';
import { CheckIcon, PencilIcon } from 'lucide-react';
import { type JSX, useState } from 'react';

import { colorSystem } from '../../theme';
import { SecondaryButton, SecondaryIconButton } from '../buttons';
import { InputAdornment } from '../InputAdornment';
import { Tooltip } from '../Tooltip';
import { BaseField, BaseFieldProps, FieldConverter } from './BaseField';
import { fieldConverterRateString, fieldConverterString } from './FieldNumeric/FieldNumeric';
import { FieldSelect } from './FieldSelect';

export interface FieldMonetaryValueProps extends Omit<BaseFieldProps<MonetaryValue | null>, 'converter'> {
    defaultCurrency: Currency;
    enableCurrencyPicker?: boolean;
    /** Reads "1,125" and "1.125" as 1.125. For unit prices. */
    isRate?: boolean;
}

export function FieldMonetaryValue({
    enableCurrencyPicker = false,
    defaultCurrency,
    isRate = false,
    ...props
}: FieldMonetaryValueProps): JSX.Element {
    const currency = props.value?.currency ?? defaultCurrency;
    const [isCurrencyPickerOpen, setIsCurrencyPickerOpen] = useState(false);

    if (isCurrencyPickerOpen && enableCurrencyPicker) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    padding: 1,
                    borderRadius: 2,
                    backgroundColor: colorSystem.blue[2],
                }}
            >
                <FieldSelect
                    fullWidth
                    options={Object.values(Currency)}
                    onChange={(newCurrency) => {
                        props.onChange({
                            amount: props.value?.amount ?? '0',
                            currency: newCurrency ?? defaultCurrency,
                        });
                    }}
                    value={currency}
                />
                <SecondaryIconButton aria-label={t`Confirm currency`} onClick={() => setIsCurrencyPickerOpen(false)}>
                    <CheckIcon />
                </SecondaryIconButton>
            </Box>
        );
    }

    return (
        <BaseField
            fullWidth
            slotProps={{
                input: {
                    endAdornment: enableCurrencyPicker ? (
                        <Tooltip title={t`Change currency`}>
                            <SecondaryButton
                                size="small"
                                onClick={() => setIsCurrencyPickerOpen(true)}
                                endIcon={<PencilIcon />}
                                sx={{ marginRight: 1 }}
                            >
                                {currency}
                            </SecondaryButton>
                        </Tooltip>
                    ) : (
                        <InputAdornment position="end">{currency}</InputAdornment>
                    ),
                },
            }}
            converter={fieldConverter(currency, isRate)}
            {...props}
        />
    );
}

export const fieldConverter = (currency: Currency, isRate = false): FieldConverter<MonetaryValue | null> => {
    const amountConverter = isRate ? fieldConverterRateString : fieldConverterString;
    return {
        parseValue: (text: string) => {
            const result = amountConverter.parseValue(text);
            if (!result.ok) {
                return { ok: false, value: null, message: result.message };
            }
            if (result.value === null) {
                return { ok: true, value: null };
            }
            return { ok: true, value: { amount: String(Number(result.value)), currency } };
        },
        formatValue: (value) => {
            return value ? amountConverter.formatValue(String(value.amount)) : '';
        },
    };
};
